import cx from 'classnames';
import TimerText from 'components/timer-text/TimerText';
import {
  INIT_THEME_CATEGORY_ID,
  INIT_THEME_DETAIL_ID,
  INIT_THEME_ID,
  ITEM_CATEGORY_SELECTABLE_PER_PAGE,
  ITEM_SELECTABLE_PER_PAGE,
  ITEM_SELECTABLE_PER_PAGE_SELECT_THEME_BEFORE_PAYMENT,
  VERTICAL_SCREEN_ITEM_SELECTABLE_PER_PAGE,
  VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { isHigher } from 'helpers/math.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import EditPhotoGridSelectItem from '../edit-photo/EditPhotoGridSelectItem';
import EditPhotoSelectCategory from '../edit-photo/EditPhotoSelectCategory';
import EditPhotoSheetPreview from '../edit-photo/EditPhotoSheetPreview';
import './select-theme.css';

function SelectTheme() {
  const dispatch = useAppDispatch();
  const { audio } = useSoundContext();
  const navigate = useNavigate();
  const { getNextPath } = useNewPhotoLifeCycleStep();
  const {
    listThemeCategoryPhotos,
    listThemePhotos,
    themeDetail,
    currentLayout,
  } = useBoothAppContext();
  const { themeCategoryId, themeId } = useAppSelector(
    (state) => state.selfeApp,
  );
  const { isVerticalScreen, isThemeBeforePayment } = useAppSelector(
    (s) => s.booth,
  );

  // Chỉ bật khi theme trước payment
  const { second } = usePageTimer({
    forwardFunc: () => {},
    isBeforeTransaction: true,
    isEnabled: !!isThemeBeforePayment,
  });

  const handleSelectCategory = (category: number | undefined) => {
    if (category) {
      audio?.playClickToSelect?.();
      dispatch(setSelfeAppStateAction({ themeCategoryId: category }));
    }
  };

  const handleSelectTheme = (id: number) => {
    audio?.playClickToSelect?.();
    dispatch(setSelfeAppStateAction({ themeId: id }));

    if (isThemeBeforePayment) navigate(getNextPath() || '');
  };

  React.useEffect(() => {
    if (themeDetail && !isThemeBeforePayment) {
      dispatch(
        setSelfeAppStateAction({
          themeDetailId: themeDetail?.id || INIT_THEME_DETAIL_ID,
        }),
      );
    }
  }, [dispatch, themeDetail, isThemeBeforePayment]);

  // Tự động chọn theme đầu tiên
  useEffect(() => {
    dispatch(
      setSelfeAppStateAction({
        themeId: listThemePhotos?.data?.[0]?.id || INIT_THEME_ID,
      }),
    );
  }, [dispatch, listThemePhotos]);

  useEffect(() => {
    dispatch(
      setSelfeAppStateAction({
        themeId: themeId || INIT_THEME_ID,
      }),
    );
  }, [dispatch, themeId]);

  // Tự động chọn theme category
  useEffect(() => {
    !themeCategoryId &&
      dispatch(
        setSelfeAppStateAction({
          themeCategoryId:
            listThemeCategoryPhotos?.data?.[0]?.id || INIT_THEME_CATEGORY_ID,
        }),
      );
  }, [dispatch, listThemeCategoryPhotos, themeCategoryId]);

  const isVerticalFrame = useMemo(
    () => isHigher(currentLayout?.height, currentLayout?.width),
    [currentLayout],
  );

  // Số theme item trên 1 page
  const themePerPage = useMemo(() => {
    if (isThemeBeforePayment) {
      return ITEM_SELECTABLE_PER_PAGE_SELECT_THEME_BEFORE_PAYMENT;
    }
    if (isVerticalScreen) {
      return isVerticalFrame
        ? VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE
        : VERTICAL_SCREEN_ITEM_SELECTABLE_PER_PAGE;
    }

    return ITEM_SELECTABLE_PER_PAGE;
  }, [isVerticalScreen, isThemeBeforePayment, isVerticalFrame]);

  return (
    <div
      className={cx({
        'edit-photo edit-photo__vertical-frame select-theme__vertical-frame':
          isVerticalScreen && isThemeBeforePayment,
      })}
    >
      <div
        className={cx('theme-page edit-page', {
          'theme-before-payment': isThemeBeforePayment,
        })}
      >
        {/* Theme trước payment có timer */}
        {isThemeBeforePayment && <TimerText second={second} />}
        <EditPhotoSelectCategory
          selectedCatId={themeCategoryId}
          listCatData={listThemeCategoryPhotos}
          catPerPage={ITEM_CATEGORY_SELECTABLE_PER_PAGE}
          onSelectCategory={handleSelectCategory}
        />
        <div className="edit-page__selection-section">
          {/* Theme trước payment nên không hiện photo sheet preview */}
          {!isThemeBeforePayment && <EditPhotoSheetPreview />}
          <EditPhotoGridSelectItem
            // khi ở theme trước payment không cần hiện active item
            selectedId={isThemeBeforePayment ? undefined : themeId}
            listData={listThemePhotos}
            itemPerPage={themePerPage}
            onSelectItem={handleSelectTheme}
          />
        </div>
      </div>
    </div>
  );
}

export default SelectTheme;
